import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ScrollToTop } from "./components/ScrollToTop";
import { lazy, Suspense } from "react";

// Main pages
import Index from "./pages/Index";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Pricing from "./pages/Pricing";
import AIAgents from "./pages/AIAgents";
import AISEOAgency from "./pages/AISEOAgency";
import ROICalculator from "./pages/ROICalculator";
import FAQs from "./pages/FAQs";
import Contact from "./pages/Contact";
import About from "./pages/About";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";

// Index Pages
const Insights = lazy(() => import("./pages/Insights"));
const Industries = lazy(() => import("./pages/Industries"));

// Conversion Assets & Resources
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const ResourceHub = lazy(() => import("./pages/ResourceHub"));
const AIGrowthAudit = lazy(() => import("./pages/tools/AIGrowthAudit"));
const AISEOGEOAudit = lazy(() => import("./pages/tools/AISEOGEOAudit"));
const SaaSGTMBlueprint = lazy(() => import("./pages/resources/SaaSGTMBlueprint"));
const SearchGrowthFramework = lazy(() => import("./pages/resources/SearchGrowthFramework"));

// GEO Money Pages
const AISEOAgencyPage = lazy(() => import("./pages/services/geo/AISEOAgencyPage"));
const SaaSSEOPage = lazy(() => import("./pages/services/geo/SaaSSEOPage"));
const B2BSEOPage = lazy(() => import("./pages/services/geo/B2BSEOPage"));
const CloudSEOPage = lazy(() => import("./pages/services/geo/CloudSEOPage"));
const EnterpriseSEOPage = lazy(() => import("./pages/services/geo/EnterpriseSEOPage"));

// AI Ads Money Pages
const B2BPerformanceMarketingPage = lazy(() => import("./pages/services/ai-ads/B2BPerformanceMarketingPage"));
const GoogleAdsSaaSPage = lazy(() => import("./pages/services/ai-ads/GoogleAdsSaaSPage"));
const GoogleAdsB2BPage = lazy(() => import("./pages/services/ai-ads/GoogleAdsB2BPage"));
const LinkedInAdsB2BPage = lazy(() => import("./pages/services/ai-ads/LinkedInAdsB2BPage"));
const PaidMediaSaaSPage = lazy(() => import("./pages/services/ai-ads/PaidMediaSaaSPage"));

// AI Content Money Pages
const AIContentCreationPage = lazy(() => import("./pages/services/ai-content/AIContentCreationPage"));
const SEOContentSaaSPage = lazy(() => import("./pages/services/ai-content/SEOContentSaaSPage"));
const B2BContentMarketingPage = lazy(() => import("./pages/services/ai-content/B2BContentMarketingPage"));
const EnterpriseSEOContentPage = lazy(() => import("./pages/services/ai-content/EnterpriseSEOContentPage"));

// AI Agents Money Pages
const AIChatbotsPage = lazy(() => import("./pages/services/ai-agents/AIChatbotsPage"));
const LeadQualificationPage = lazy(() => import("./pages/services/ai-agents/LeadQualificationPage"));
const SalesAgentsPage = lazy(() => import("./pages/services/ai-agents/SalesAgentsPage"));
const CustomerSupportPage = lazy(() => import("./pages/services/ai-agents/CustomerSupportPage"));

// Industry Pages
const SEOSaaSCompaniesPage = lazy(() => import("./pages/industries/SEOSaaSCompaniesPage"));
const SEOB2BCompaniesPage = lazy(() => import("./pages/industries/SEOB2BCompaniesPage"));
const SEOCloudInfrastructurePage = lazy(() => import("./pages/industries/SEOCloudInfrastructurePage"));
const SEOAIStartupsPage = lazy(() => import("./pages/industries/SEOAIStartupsPage"));
const SEOEnterpriseSoftwarePage = lazy(() => import("./pages/industries/SEOEnterpriseSoftwarePage"));
const PaidAdsSaaSPage = lazy(() => import("./pages/industries/PaidAdsSaaSPage"));
const PaidAdsB2BSoftwarePage = lazy(() => import("./pages/industries/PaidAdsB2BSoftwarePage"));
const PerformanceMarketingSaaSPage = lazy(() => import("./pages/industries/PerformanceMarketingSaaSPage"));
const ContentMarketingSaaSPage = lazy(() => import("./pages/industries/ContentMarketingSaaSPage"));
const ContentMarketingB2BPage = lazy(() => import("./pages/industries/ContentMarketingB2BPage"));
const SEOContentTechnologyPage = lazy(() => import("./pages/industries/SEOContentTechnologyPage"));
const AIAgentsSaaSPage = lazy(() => import("./pages/industries/AIAgentsSaaSPage"));
const AIAgentsB2BPage = lazy(() => import("./pages/industries/AIAgentsB2BPage"));
const AIAgentsEnterprisePage = lazy(() => import("./pages/industries/AIAgentsEnterprisePage"));

// Insight Pages
const WhatIsGEOPage = lazy(() => import("./pages/insights/WhatIsGEOPage"));
const HowLLMSearchWorksPage = lazy(() => import("./pages/insights/HowLLMSearchWorksPage"));
const SEOForChatGPTPage = lazy(() => import("./pages/insights/SEOForChatGPTPage"));
const AISearchVsGooglePage = lazy(() => import("./pages/insights/AISearchVsGooglePage"));
const FutureOfSearchVisibilityPage = lazy(() => import("./pages/insights/FutureOfSearchVisibilityPage"));
const AIChangingPerformanceMarketingPage = lazy(() => import("./pages/insights/AIChangingPerformanceMarketingPage"));
const WhyPaidAdsFailPage = lazy(() => import("./pages/insights/WhyPaidAdsFailPage"));
const AIVsManualAdOptimizationPage = lazy(() => import("./pages/insights/AIVsManualAdOptimizationPage"));
const AIContentVsHumanContentPage = lazy(() => import("./pages/insights/AIContentVsHumanContentPage"));
const WhyAIContentDoesntRankPage = lazy(() => import("./pages/insights/WhyAIContentDoesntRankPage"));
const ContentSystemsVsBloggingPage = lazy(() => import("./pages/insights/ContentSystemsVsBloggingPage"));
const WhatAreAIAgentsPage = lazy(() => import("./pages/insights/WhatAreAIAgentsPage"));
const AIAgentsVsChatbotsPage = lazy(() => import("./pages/insights/AIAgentsVsChatbotsPage"));
const AIAgentsIncreaseConversionsPage = lazy(() => import("./pages/insights/AIAgentsIncreaseConversionsPage"));

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" forcedTheme="dark">
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Main Routes */}
              <Route path="/" element={<Index />} />
              <Route path="/services" element={<Services />} />
              <Route path="/services/:slug" element={<ServiceDetail />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/ai-agents" element={<AIAgents />} />
              <Route path="/ai-seo-agency" element={<AISEOAgency />} />
              <Route path="/roi-calculator" element={<ROICalculator />} />
              <Route path="/resources" element={<ResourceHub />} />
              <Route path="/faqs" element={<FAQs />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/about" element={<About />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />

              {/* Index Pages */}
              <Route path="/insights" element={<Insights />} />
              <Route path="/industries" element={<Industries />} />

              {/* Conversion Assets */}
              <Route path="/case-studies" element={<CaseStudies />} />
              <Route path="/tools/ai-growth-audit" element={<AIGrowthAudit />} />
              <Route path="/tools/ai-seo-geo-audit" element={<AISEOGEOAudit />} />
              <Route path="/resources/saas-gtm-blueprint" element={<SaaSGTMBlueprint />} />
              <Route path="/resources/search-growth-framework" element={<SearchGrowthFramework />} />

              {/* GEO Money Pages */}
              <Route path="/services/geo/ai-seo-agency" element={<AISEOAgencyPage />} />
              <Route path="/services/geo/saas-seo" element={<SaaSSEOPage />} />
              <Route path="/services/geo/b2b-seo" element={<B2BSEOPage />} />
              <Route path="/services/geo/cloud-seo" element={<CloudSEOPage />} />
              <Route path="/services/geo/enterprise-seo" element={<EnterpriseSEOPage />} />

              {/* AI Ads Money Pages */}
              <Route path="/services/ai-ads/b2b-performance-marketing" element={<B2BPerformanceMarketingPage />} />
              <Route path="/services/ai-ads/google-ads-saas" element={<GoogleAdsSaaSPage />} />
              <Route path="/services/ai-ads/google-ads-b2b" element={<GoogleAdsB2BPage />} />
              <Route path="/services/ai-ads/linkedin-ads-b2b" element={<LinkedInAdsB2BPage />} />
              <Route path="/services/ai-ads/paid-media-saas" element={<PaidMediaSaaSPage />} />

              {/* AI Content Money Pages */}
              <Route path="/services/ai-content/ai-content-creation" element={<AIContentCreationPage />} />
              <Route path="/services/ai-content/seo-content-saas" element={<SEOContentSaaSPage />} />
              <Route path="/services/ai-content/b2b-content-marketing" element={<B2BContentMarketingPage />} />
              <Route path="/services/ai-content/enterprise-seo-content" element={<EnterpriseSEOContentPage />} />

              {/* AI Agents Money Pages */}
              <Route path="/services/ai-agents/ai-chatbots" element={<AIChatbotsPage />} />
              <Route path="/services/ai-agents/lead-qualification" element={<LeadQualificationPage />} />
              <Route path="/services/ai-agents/sales-agents" element={<SalesAgentsPage />} />
              <Route path="/services/ai-agents/customer-support" element={<CustomerSupportPage />} />

              {/* Industry Pages */}
              <Route path="/industries/seo-saas-companies" element={<SEOSaaSCompaniesPage />} />
              <Route path="/industries/seo-b2b-companies" element={<SEOB2BCompaniesPage />} />
              <Route path="/industries/seo-cloud-infrastructure" element={<SEOCloudInfrastructurePage />} />
              <Route path="/industries/seo-ai-startups" element={<SEOAIStartupsPage />} />
              <Route path="/industries/seo-enterprise-software" element={<SEOEnterpriseSoftwarePage />} />
              <Route path="/industries/paid-ads-saas" element={<PaidAdsSaaSPage />} />
              <Route path="/industries/paid-ads-b2b-software" element={<PaidAdsB2BSoftwarePage />} />
              <Route path="/industries/performance-marketing-saas" element={<PerformanceMarketingSaaSPage />} />
              <Route path="/industries/content-marketing-saas" element={<ContentMarketingSaaSPage />} />
              <Route path="/industries/content-marketing-b2b" element={<ContentMarketingB2BPage />} />
              <Route path="/industries/seo-content-technology" element={<SEOContentTechnologyPage />} />
              <Route path="/industries/ai-agents-saas" element={<AIAgentsSaaSPage />} />
              <Route path="/industries/ai-agents-b2b" element={<AIAgentsB2BPage />} />
              <Route path="/industries/ai-agents-enterprise" element={<AIAgentsEnterprisePage />} />

              {/* Insight Pages */}
              <Route path="/insights/what-is-geo" element={<WhatIsGEOPage />} />
              <Route path="/insights/how-llm-search-works" element={<HowLLMSearchWorksPage />} />
              <Route path="/insights/seo-for-chatgpt" element={<SEOForChatGPTPage />} />
              <Route path="/insights/ai-search-vs-google" element={<AISearchVsGooglePage />} />
              <Route path="/insights/future-of-search-visibility" element={<FutureOfSearchVisibilityPage />} />
              <Route path="/insights/ai-changing-performance-marketing" element={<AIChangingPerformanceMarketingPage />} />
              <Route path="/insights/why-paid-ads-fail" element={<WhyPaidAdsFailPage />} />
              <Route path="/insights/ai-vs-manual-ad-optimization" element={<AIVsManualAdOptimizationPage />} />
              <Route path="/insights/ai-content-vs-human-content" element={<AIContentVsHumanContentPage />} />
              <Route path="/insights/why-ai-content-doesnt-rank" element={<WhyAIContentDoesntRankPage />} />
              <Route path="/insights/content-systems-vs-blogging" element={<ContentSystemsVsBloggingPage />} />
              <Route path="/insights/what-are-ai-agents" element={<WhatAreAIAgentsPage />} />
              <Route path="/insights/ai-agents-vs-chatbots" element={<AIAgentsVsChatbotsPage />} />
              <Route path="/insights/ai-agents-increase-conversions" element={<AIAgentsIncreaseConversionsPage />} />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
